<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import FileDropLayer from '@/modules/files/components/FileDropLayer.vue'
import { useFormFileDrop } from '@/modules/files/composables/useFormFileDrop'
import DocumentAddVersionModal from './modals/DocumentAddVersionModal.vue'
import DocumentAudioPlayer from '@/modules/documents-new/components/DocumentAudioPlayer.vue'
import DocumentTexts from '@/modules/documents-new/components/DocumentTexts.vue'
import FolderSelectionModal from './modals/FolderSelectionModal.vue'
import NewTreeMultiSelectComponent from '@/components/createdUi/NewTreeMultiSelectComponent.vue'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import { BASE_URL } from '@/environment/environment'
import { DocumentsApi } from '@/modules/documents-new/services/documentsApi'
import {
  type AccessEntity,
  type CreateDocumentPayload,
  type DocumentAcknowledgementItem,
  type DocumentCreator,
  type DocumentType,
  type DocumentVersion,
  type UpdateDocumentPayload,
  getDocumentEdition,
} from '@/modules/documents-new/types/document.types'
import type { DocumentTreeItem } from '@/modules/documents-new/types/tree.types'
import { useFileUpload } from '@/modules/documents-new/composables'
import { ReferencesApi } from '@/modules/documents-new/services/referencesApi'
import { formatFileSize } from '@/modules/documents-new/utils/fileHelpers'
import { mapDocumentFamiliarizers } from '@/modules/documents-new/utils/mapAcknowledgement'
import { resolveFileName } from '@/modules/files/utils/fileNameHelpers'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useRznSectionsStore } from '@/modules/documents-new/stores/rznSectionsStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import universal from '@images/icons/files/universal.svg'
import xsl from '@images/icons/files/xls.svg'
import pdf from '@images/icons/files/pdf.svg'
import docIcon from '@images/icons/files/doc.svg'
import jpg from '@images/icons/files/jpg.svg'
import type { FolderItem } from '@/modules/documents-new/types/folder.types'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import {
  DOCUMENT_EDITION_MAX_LENGTH,
  getDocumentStatusActionTooltip,
  getDocumentStatusConfig,
  getDocumentStatusLabel,
  getDocumentStatusTransition,
  isDraftDocumentStatus,
} from '@/modules/documents-new/constants/documentStatuses'
import CopyEntityLink from '@/components/createdUi/CopyEntityLink.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import CustomSelectedItems from '@/components/createdUi/CustomSelectedItems.vue'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import ParticipantSelectionModal from '@/components/createdUi/ParticipantSelectionModal.vue'
import type {
  IParticipantNamedItem,
  IParticipantSelectionResult,
} from '@/components/createdUi/ParticipantSelectionModal.vue'
import { useFormEmbeddedLayout } from '@/composables/ui/useFormEmbeddedLayout'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { ERouteNames } from '@/router/ERouteNames'
import { useNotifications } from '@/modules/feedback/composables/useNotifications'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { formatDateOnly, getFullName } from '@/utils/formatters'

type Mode = 'create' | 'edit'

interface Props {
  isOpen: boolean
  mode: Mode
  documentId?: string | null
  folders: FolderItem[]
  initialFolderId?: string | null
  canEdit?: boolean
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'created', folderId: string | null): void
  (e: 'updated'): void
  (e: 'deleted'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { xs } = useDisplay()

const feedbackStore = useFeedbackStore()
const apiStore = useApiStore()
const rznSectionsStore = useRznSectionsStore()
const userStore = useUserStore()
const { showError, showSuccess, showWarning } = useNotifications()

const { sectionsTree } = storeToRefs(rznSectionsStore)
const isFetchingSections = computed(() => rznSectionsStore.loading)

/** DocumentTreeItem → ITreeNode для NewTreeMultiSelectComponent */
function mapSectionsToTreeNodes(items: DocumentTreeItem[]): ITreeNode[] {
  return items.map(item => ({
    key: String(item.id),
    label: item.label,
    data: {
      name: item.label,
      selfId: String(item.id),
    },
    selectable: true,
    children: item.children?.length ? mapSectionsToTreeNodes(item.children) : [],
  }))
}

const rznSectionsTreeData = computed(() => mapSectionsToTreeNodes(sectionsTree.value))

const rootSectionIds = computed(() => new Set(sectionsTree.value.map(s => String(s.id))))

const mapAccessEntity = (item: AccessEntity): IParticipantNamedItem => ({
  id: String(item.id),
  name: item.short_name?.trim() || item.name?.trim() || String(item.id),
})

const loadRznSections = async () => {
  try {
    await rznSectionsStore.fetchSections({ depth: 'section' })
    if (!sectionsTree.value.length) {
      feedbackStore.showToast({
        type: 'error',
        message: 'Не удалось загрузить разделы нормативов',
        time: 3000,
      })
    }
  } catch (e) {
    console.error('Ошибка загрузки разделов нормативов:', e)
    showError(e, 'Загрузка разделов нормативов')
  }
}

const { fileInput, fileInputRef, onFileSelect, openFileDialog, removeFile }
  = useFileUpload()

const formCardRef = ref<{ $el?: HTMLElement } | null>(null)

interface AttachmentFileItem {
  file: File
  filename: string
}

const ATTACHMENTS_PREVIEW_COUNT = 2

const attachmentFiles = ref<AttachmentFileItem[]>([])
const attachmentInputRef = ref<HTMLInputElement | null>(null)
const areAllAttachmentsVisible = ref(false)

const documentTypes = ref<DocumentType[]>([])
const isFetchingTypes = ref(false)

const documentTypesOptions = computed(() =>
  documentTypes.value.map(type => ({
    title: type.name,
    value: type.id,
  })),
)

const loadDocumentTypes = async (force = false) => {
  if (isFetchingTypes.value) {
    return
  }
  if (!force && documentTypes.value.length) {
    return
  }

  isFetchingTypes.value = true
  try {
    documentTypes.value = await ReferencesApi.fetchTypes({ ordering: 'name' })
  } catch (e) {
    console.error('Ошибка загрузки типов документов:', e)
  } finally {
    isFetchingTypes.value = false
  }
}

const formData = ref({
  edition: '',
  number: '',
  name: '',
  type_id: null as number | null,
  folder_id: null as string | null,
  section_ids: [] as string[],
  approved_at: '',
  valid_until: '',
  effective_at: '',
})

const isAvailableToAll = ref(true)
const selectedEmployees = ref<IEmployeeTreeItem[]>([])
const selectedDepartments = ref<IParticipantNamedItem[]>([])
const selectedPositions = ref<IParticipantNamedItem[]>([])
const selectedFolderName = ref<string>('')
const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)
const participantModalRef = ref<InstanceType<typeof ParticipantSelectionModal> | null>(null)
const folderModalRef = ref<InstanceType<typeof FolderSelectionModal>>()

const selectableFolders = computed(() =>
  props.folders.filter(item => {
    const name = item.name.trim().toLowerCase()

    return !name.includes('глобал') && !name.includes('global')
  }),
)
const isSubmitting = ref(false)
const existingFileRemoved = ref(false)
const isFetchingDetails = ref(false)
const isAdditionalInfoExpanded = ref(false)
const selectedDevelopers = ref<IEmployeeTreeItem[]>([])
const selectedDeveloperIds = computed(() => selectedDevelopers.value.map(d => String(d.id)))
const selectedDevelopersDisplay = computed(() =>
  selectedDevelopers.value.map(d => d.full_name).filter(Boolean).join(', '),
)
const formRef = ref()
const dismissSuppressedUntil = ref(0)
const DISMISS_SUPPRESS_MS = 300

const suppressDismissTemporarily = (reason?: string) => {
  dismissSuppressedUntil.value = Date.now() + DISMISS_SUPPRESS_MS
  if (reason) {
    console.log('[DocumentFormSidebar] dismiss suppression set', { reason })
  }
}

const documentDetails = ref<{
  name: string
  versions: DocumentVersion[]
  status?: string
  created_by?: DocumentCreator
  created_at?: string
} | null>(null)

const isEmployeeProfileOpen = ref(false)
const employeeProfileId = ref('')
const isTextsOpen = ref(false)
const isHistoryOpen = ref(false)
const isAcknowledgementOpen = ref(false)
const acknowledgementItems = ref<DocumentAcknowledgementItem[]>([])
const isAcknowledgementLoading = ref(false)
const isAcknowledging = ref(false)
const isAssigningFamiliarizers = ref(false)
const isAddRevisionModalOpen = ref(false)
const familiarizerModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

const isSidePaneOpen = computed(() => isHistoryOpen.value || isAcknowledgementOpen.value || isTextsOpen.value)

const selectedFamiliarizerEmployees = computed<IEmployeeTreeItem[]>(() =>
  acknowledgementItems.value
    .filter(item => item.employeeId)
    .map(item => ({
      id: String(item.employeeId),
      full_name: item.employeeName,
      position_name: '',
    })),
)

const toggleTextsPane = () => {
  isTextsOpen.value = !isTextsOpen.value
  isHistoryOpen.value = false
  isAcknowledgementOpen.value = false
}

const openHistoryPane = () => {
  isTextsOpen.value = false
  isAcknowledgementOpen.value = false
  isHistoryOpen.value = true
}

const closeHistoryPane = () => {
  isHistoryOpen.value = false
}

const toggleHistoryPane = () => {
  if (isHistoryOpen.value) {
    closeHistoryPane()
    return
  }
  openHistoryPane()
}

const loadAcknowledgementSheet = async () => {
  if (!props.documentId) {
    acknowledgementItems.value = []
    return
  }

  isAcknowledgementLoading.value = true
  try {
    const data = await DocumentsApi.fetchDocumentFamiliarizers(props.documentId)
    acknowledgementItems.value = mapDocumentFamiliarizers(data)
  }
  catch (error) {
    acknowledgementItems.value = []
    showError(error, 'Загрузка листа ознакомления')
  }
  finally {
    isAcknowledgementLoading.value = false
  }
}

const openAcknowledgementPane = async () => {
  isTextsOpen.value = false
  isHistoryOpen.value = false
  isAcknowledgementOpen.value = true
  await loadAcknowledgementSheet()
}

const closeAcknowledgementPane = () => {
  isAcknowledgementOpen.value = false
}

const toggleAcknowledgementPane = async () => {
  if (isAcknowledgementOpen.value) {
    closeAcknowledgementPane()
    return
  }
  await openAcknowledgementPane()
}

const formatAcknowledgementDate = (dateStr?: string | null): string => formatDateOnly(dateStr)

const onEditAcknowledgementClick = () => {
  if (!props.canEdit || !props.documentId)
    return
  familiarizerModalRef.value?.open()
}

const onFamiliarizersSelected = async (employees: IEmployeeTreeItem[]) => {
  if (!props.documentId)
    return

  isAssigningFamiliarizers.value = true
  try {
    await DocumentsApi.assignDocumentFamiliarizers(props.documentId, {
      responsible_employee_ids: employees.map(employee => String(employee.id)),
    })
    await loadAcknowledgementSheet()
    showSuccess('Лист ознакомления обновлен')
  }
  catch (error) {
    showError(error, 'Назначение на ознакомление')
  }
  finally {
    isAssigningFamiliarizers.value = false
  }
}

const onAcknowledgeClick = async () => {
  if (!props.documentId) {
    return
  }

  const currentUserId = userStore.user?.id
  const myEntry = acknowledgementItems.value.find(item =>
    item.status === 'pending'
    && (
      (currentUserId && item.employeeId === currentUserId)
      || item.actions?.includes('acknowledge')
      || item.actions?.includes('complete')
    ),
  )

  if (!myEntry) {
    showWarning('Нет доступной записи для ознакомления')
    return
  }

  isAcknowledging.value = true
  try {
    await DocumentsApi.acknowledgeDocumentFamiliarizer(props.documentId, myEntry.id)
    await loadAcknowledgementSheet()
    showSuccess('Ознакомление подтверждено')
  }
  catch (error) {
    showError(error, 'Подтверждение ознакомления')
  }
  finally {
    isAcknowledging.value = false
  }
}
const isDeleteConfirmOpen = ref(false)
const isDeleting = ref(false)
const isChangingStatus = ref(false)

const getFileIcon = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'xls':
    case 'xlsx':
      return xsl
    case 'pdf':
      return pdf
    case 'doc':
    case 'docx':
      return docIcon
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return jpg
    default:
      return universal
  }
}

const rules = {
  name: [(v: string) => !!v || 'Наименование обязательно'],
  type_id: [(v: number) => !!v || 'Тип обязателен'],
  folder_id: [(v: string) => !!v?.trim() || 'Папка обязательна'],
  file: [(v: string) => !!v?.trim() || 'Файл обязателен'],
}

const isEditMode = computed(() => props.mode === 'edit')
const isFormReadonly = computed(() => isEditMode.value && !props.canEdit)

const formEmbeddedLayout = useFormEmbeddedLayout(isEditMode)

/** На мобилке боковая панель полностью подменяет форму; на десктопе — как раньше справа */
const isModalSideOpen = computed(() =>
  isEditMode.value
  && isSidePaneOpen.value
  && xs.value
  && !formEmbeddedLayout.isEmbedded.value,
)

const documentFormMainRef = ref<HTMLElement | null>(null)

const CREATE_FORM_MAX_WIDTH = 640

const dialogMaxWidth = computed(() => {
  if (formEmbeddedLayout.isEmbedded.value) {
    return undefined
  }

  return isEditMode.value
    ? 670
    : CREATE_FORM_MAX_WIDTH
})

/** Геометрия как у НС с закрытым чатом (`entity-form-embedded--no-chat`). */
const dialogContentClass = computed(() => {
  if (formEmbeddedLayout.isEmbedded.value && formEmbeddedLayout.contentClass.value) {
    const classes: string[] = [formEmbeddedLayout.contentClass.value, 'entity-form-embedded--no-chat']
    if (isSidePaneOpen.value) {
      classes.push('entity-form-embedded--history-open')
    }

    return classes.join(' ')
  }

  if (isEditMode.value && xs.value) {
    return 'document-form-dialog--mobile'
  }

  return undefined
})

const shareDocumentTo = computed(() => {
  if (!props.documentId || !isEditMode.value) {
    return undefined
  }

  const folderId = formData.value.folder_id ?? props.initialFolderId ?? null
  const query = { document: String(props.documentId) }

  if (folderId == null) {
    return { name: ERouteNames.DOCUMENTS, query }
  }

  return {
    name: ERouteNames.DOCUMENTS_FOLDER,
    params: { folderId: String(folderId) },
    query,
  }
})

const documentHeaderName = computed(() => formData.value.name?.trim() || '')

const isEditingName = ref(false)
const nameInput = ref('')

const canEditName = computed(() => isEditMode.value && !isFormReadonly.value)

const startEditName = () => {
  if (!canEditName.value) {
    return
  }
  nameInput.value = formData.value.name || ''
  isEditingName.value = true
}

const cancelEditName = () => {
  isEditingName.value = false
  nameInput.value = formData.value.name || ''
}

const saveName = () => {
  if (!isEditingName.value) {
    return
  }
  const next = nameInput.value.trim()
  isEditingName.value = false
  if (!next) {
    nameInput.value = formData.value.name || ''
    return
  }
  formData.value.name = next
}

const submitButtonText = computed(() =>
  isEditMode.value ? 'Сохранить' : 'Добавить',
)

const sortedDocumentTypesOptions = computed(() =>
  [...documentTypesOptions.value].sort((a, b) =>
    a.title.localeCompare(b.title, 'ru', { sensitivity: 'base' }),
  ),
)

const accessType = computed({
  get: () => (isAvailableToAll.value ? 'all' : 'limited'),
  set: (value: 'all' | 'limited') => {
    if (isFormReadonly.value) {
      return
    }
    isAvailableToAll.value = value === 'all'
  },
})

const hasMainDocument = computed(() => !!fileInput.value)

const isCreateSubmitDisabled = computed(
  () => !isEditMode.value && !hasMainDocument.value,
)

interface EditFormSnapshot {
  formData: typeof formData.value
  isAvailableToAll: boolean
  selectedEmployeeIds: string[]
  selectedDepartmentIds: string[]
  selectedPositionIds: string[]
  selectedDeveloperIds: string[]
  existingFileRemoved: boolean
  hasNewFile: boolean
  attachmentCount: number
}

const initialEditFormSnapshot = ref<EditFormSnapshot | null>(null)

const sortedStringArray = (values: string[]) => [...values].sort()

const areSortedArraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) {
    return false
  }

  return a.every((value, index) => value === b[index])
}

const captureEditFormSnapshot = () => {
  initialEditFormSnapshot.value = {
    formData: {
      ...formData.value,
      section_ids: [...formData.value.section_ids],
    },
    isAvailableToAll: isAvailableToAll.value,
    selectedEmployeeIds: sortedStringArray(selectedEmployees.value.map(e => String(e.id))),
    selectedDepartmentIds: sortedStringArray(selectedDepartments.value.map(d => d.id)),
    selectedPositionIds: sortedStringArray(selectedPositions.value.map(p => p.id)),
    selectedDeveloperIds: sortedStringArray(selectedDeveloperIds.value),
    existingFileRemoved: existingFileRemoved.value,
    hasNewFile: !!fileInput.value,
    attachmentCount: attachmentFiles.value.length,
  }
}

const isEditFormDirty = computed(() => {
  const snapshot = initialEditFormSnapshot.value
  if (!isEditMode.value || !snapshot) {
    return false
  }

  const current = formData.value
  const initial = snapshot.formData

  if (
    current.number !== initial.number
    || current.name !== initial.name
    || current.type_id !== initial.type_id
    || current.folder_id !== initial.folder_id
    || current.edition.trim() !== initial.edition.trim()
    || current.approved_at !== initial.approved_at
    || current.valid_until !== initial.valid_until
    || current.effective_at !== initial.effective_at
  ) {
    return true
  }

  if (!areSortedArraysEqual(
    sortedStringArray(current.section_ids),
    sortedStringArray(initial.section_ids),
  )) {
    return true
  }

  if (isAvailableToAll.value !== snapshot.isAvailableToAll) {
    return true
  }

  if (!areSortedArraysEqual(
    sortedStringArray(selectedEmployees.value.map(e => String(e.id))),
    snapshot.selectedEmployeeIds,
  )) {
    return true
  }

  if (!areSortedArraysEqual(
    sortedStringArray(selectedDepartments.value.map(d => d.id)),
    snapshot.selectedDepartmentIds,
  )) {
    return true
  }

  if (!areSortedArraysEqual(
    sortedStringArray(selectedPositions.value.map(p => p.id)),
    snapshot.selectedPositionIds,
  )) {
    return true
  }

  if (!areSortedArraysEqual(
    sortedStringArray(selectedDeveloperIds.value),
    snapshot.selectedDeveloperIds,
  )) {
    return true
  }

  if (
    existingFileRemoved.value !== snapshot.existingFileRemoved
    || !!fileInput.value !== snapshot.hasNewFile
    || attachmentFiles.value.length !== snapshot.attachmentCount
  ) {
    return true
  }

  return false
})

const isSubmitDisabled = computed(() => {
  if (isEditMode.value && isFetchingDetails.value) {
    return true
  }

  if (isEditMode.value) {
    return !props.canEdit || !isEditFormDirty.value
  }

  return isCreateSubmitDisabled.value
})

const statusChipConfig = computed(() =>
  documentDetails.value?.status
    ? getDocumentStatusConfig(documentDetails.value.status)
    : null,
)

const loadDocumentData = async () => {
  if (!props.documentId || !isEditMode.value) {
    return
  }

  isFetchingDetails.value = true
  initialEditFormSnapshot.value = null
  feedbackStore.isGlobalLoading = true
  try {
    if (!apiStore.employees?.length) {
      await apiStore.fetchAllEmployees()
    }
    await loadDocumentTypes()

    const detail = await DocumentsApi.fetchDocumentDetails(props.documentId)

    documentDetails.value = {
      name: detail.name,
      versions: detail.versions || [],
      status: detail.status,
      created_by: detail.created_by,
      created_at: detail.created_at,
    }

    const raw = detail as unknown as Record<string, unknown>
    let sectionIds: string[] = []

    if (Array.isArray(raw.sections) && raw.sections.length) {
      sectionIds = raw.sections.map((s: any) => String(s.id || s))
    } else if (raw.section) {
      if (typeof raw.section === 'object' && raw.section !== null && 'id' in raw.section) {
        sectionIds = [String((raw.section as { id: string | number }).id)]
      }
    } else if (raw.standard) {
      if (typeof raw.standard === 'object' && raw.standard !== null && 'id' in raw.standard) {
        sectionIds = [String((raw.standard as { id: string | number }).id)]
      }
    }

    const sortedVersions = [...(detail.versions || [])].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime()
      const dateB = new Date(b.created_at).getTime()

      return dateB - dateA
    })
    const currentVersion = sortedVersions.find(v => v.file) || sortedVersions[0] || null

    formData.value = {
      edition: currentVersion ? getDocumentEdition(currentVersion) : '',
      number: detail.number ?? '',
      name: detail.name,
      type_id: detail.type?.id || null,
      folder_id: detail.folder?.id || null,
      section_ids: sectionIds,
      approved_at: normalizeFormDate(currentVersion?.approved_at),
      valid_until: normalizeFormDate(currentVersion?.valid_until),
      effective_at: normalizeFormDate(currentVersion?.effective_at),
    }

    if (detail.folder) {
      selectedFolderName.value = detail.folder.name
    }

    if (Array.isArray(detail.authors) && detail.authors.length > 0) {
      selectedDevelopers.value = detail.authors.map(author => ({
        id: String(author.id),
        full_name: getFullName(author),
        position_name: author.position_name || '',
      }))
    } else if (detail.author) {
      selectedDevelopers.value = [{
        id: String(detail.author.id),
        full_name: getFullName(detail.author),
        position_name: detail.author.position_name || '',
      }]
    } else {
      selectedDevelopers.value = []
    }

    if (raw.global_access === true) {
      isAvailableToAll.value = true
      selectedEmployees.value = []
      selectedDepartments.value = []
      selectedPositions.value = []
    } else {
      isAvailableToAll.value = false

      const possibleKeys = [
        'employee_access',
        'employees',
        'allowed_employees',
        'document_employees',
      ]

      let list: Record<string, unknown>[] = []
      for (const key of possibleKeys) {
        const val = raw[key]
        if (Array.isArray(val) && val.length) {
          list = val as Record<string, unknown>[]
          break
        }
      }

      selectedEmployees.value = list.map((emp: Record<string, unknown>) => {
        const last_name = typeof emp.last_name === 'string' ? emp.last_name : ''
        const first_name = typeof emp.first_name === 'string' ? emp.first_name : ''
        const middle_name = typeof emp.middle_name === 'string' ? emp.middle_name : ''

        return {
          id: String(emp.id),
          full_name:
            (typeof emp.full_name === 'string' && emp.full_name)
            || getFullName({
              last_name,
              first_name,
              middle_name,
            }),
          position_name: typeof emp.position_name === 'string' ? emp.position_name : '',
        }
      })
    }

    if (Array.isArray(raw.department_access) && raw.department_access.length) {
      selectedDepartments.value = (raw.department_access as AccessEntity[]).map(mapAccessEntity)
    } else {
      selectedDepartments.value = []
    }

    if (Array.isArray(raw.position_access) && raw.position_access.length) {
      selectedPositions.value = (raw.position_access as AccessEntity[]).map(mapAccessEntity)
    } else {
      selectedPositions.value = []
    }

    await loadRznSections()
    captureEditFormSnapshot()
  } catch (error) {
    console.error('Ошибка загрузки документа:', error)
    showError(error, 'Загрузка документа')
  } finally {
    isFetchingDetails.value = false
    feedbackStore.isGlobalLoading = false
  }
}

const lastVersion = computed<DocumentVersion | null>(() => {
  if (!documentDetails.value?.versions?.length) {
    return null
  }

  const sorted = [...documentDetails.value.versions].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime()
    const dateB = new Date(b.created_at).getTime()

    return dateB - dateA
  })

  return sorted.find(v => v.file) || null
})

const getVersionFileDisplayName = (version: DocumentVersion): string =>
  resolveFileName(version.file, version.filename)

const formatRevisionLabel = (version?: string | null) => {
  const value = version?.trim()
  if (!value) {
    return null
  }

  return value.replace(/^версия\s+/i, '')
}

const versionChipLabel = computed(() =>
  formatRevisionLabel(lastVersion.value ? getDocumentEdition(lastVersion.value) : null),
)

const currentRevisionLabel = computed(() => {
  const edition = lastVersion.value ? getDocumentEdition(lastVersion.value).trim() : ''
  return edition || null
})

const sortedVersions = computed(() => {
  if (!documentDetails.value?.versions?.length) {
    return []
  }

  return [...documentDetails.value.versions].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime()
    const dateB = new Date(b.created_at).getTime()

    return dateB - dateA
  })
})

const canChangeArchiveStatus = computed(() => {
  if (!props.canEdit) {
    return false
  }

  return getDocumentStatusTransition(documentDetails.value?.status) !== null
})

const archiveTooltip = computed(() =>
  getDocumentStatusActionTooltip(documentDetails.value?.status),
)

const hasEditMainFile = computed(
  () => (!!lastVersion.value && !existingFileRemoved.value) || hasMainDocument.value,
)

const visibleAttachmentEntries = computed(() => {
  const list = attachmentFiles.value
  const visible = areAllAttachmentsVisible.value || list.length <= ATTACHMENTS_PREVIEW_COUNT
    ? list
    : list.slice(0, ATTACHMENTS_PREVIEW_COUNT)

  return visible.map((item, index) => ({ item, index }))
})

const hasHiddenAttachments = computed(() =>
  !areAllAttachmentsVisible.value && attachmentFiles.value.length > ATTACHMENTS_PREVIEW_COUNT,
)

const findFolderById = (folderId: string | null): FolderItem | null => {
  if (folderId === null || folderId === '') {
    return null
  }

  const targetId = String(folderId)
  const findInTree = (items: FolderItem[]): FolderItem | null => {
    for (const item of items) {
      if (String(item.id) === targetId) {
        return item
      }
      if (item.children?.length) {
        const found = findInTree(item.children)
        if (found) {
          return found
        }
      }
    }

    return null
  }

  return findInTree(props.folders)
}

const getFolderPath = (items: FolderItem[], targetId: string): FolderItem[] | null => {
  const target = String(targetId)
  for (const item of items) {
    if (String(item.id) === target) {
      return [item]
    }
    if (item.children?.length) {
      const found = getFolderPath(item.children, targetId)
      if (found) {
        return [item, ...found]
      }
    }
  }

  return null
}

const folderBreadcrumbs = computed(() => {
  const id = formData.value.folder_id
  if (id == null) {
    return []
  }
  const path = getFolderPath(props.folders, id)

  return path ? path.map(f => f.name) : []
})

watch(
  () => props.isOpen,
  async isOpen => {
    if (!isOpen) {
      return
    }
    if (!apiStore.employees?.length) {
      await apiStore.fetchAllEmployees()
    }
    await loadDocumentTypes(true)

    // Редактирование: грузим документ, разделы загрузятся через watch на type_id
    if (isEditMode.value && props.documentId) {
      await loadDocumentData()
    } else {
      if (props.initialFolderId !== undefined) {
        const folder = findFolderById(props.initialFolderId)
        if (folder) {
          formData.value.folder_id = folder.id
          selectedFolderName.value = folder.name
        }
      }

      // Создание: дерево нормативов — глобальный оверлей как при открытии НС
      feedbackStore.isGlobalLoading = true
      try {
        await loadRznSections()
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    }
  },
  { immediate: true },
)

// Загружать дерево разделов при изменении типа документа
watch(
  () => formData.value.type_id,
  async () => {
    // Пропускаем первый вызов при монтировании, если type_id не установлен
    if (!props.isOpen) {
      return
    }

    await loadRznSections()
  },
)

const resetForm = () => {
  setTimeout(() => {
    formData.value = {
      edition: '',
      number: '',
      name: '',
      type_id: null,
      folder_id: null,
      section_ids: [],
      approved_at: '',
      valid_until: '',
      effective_at: '',
    }
    isAvailableToAll.value = true
    selectedEmployees.value = []
    selectedDepartments.value = []
    selectedPositions.value = []
    selectedFolderName.value = ''
    selectedDevelopers.value = []
    isAdditionalInfoExpanded.value = false
    documentDetails.value = null
    removeFile()
    attachmentFiles.value = []
    areAllAttachmentsVisible.value = false
    if (attachmentInputRef.value) {
      attachmentInputRef.value.value = ''
    }
    existingFileRemoved.value = false
    initialEditFormSnapshot.value = null
    isEditingName.value = false
    nameInput.value = ''
    isTextsOpen.value = false
    isHistoryOpen.value = false
    isAcknowledgementOpen.value = false
    acknowledgementItems.value = []
    formRef.value?.resetValidation()
  }, 500)
}

const openAttachmentDialog = () => {
  const canAddAttachments = isEditMode.value
    ? hasEditMainFile.value
    : hasMainDocument.value
  if (!canAddAttachments) {
    return
  }
  attachmentInputRef.value?.click()
}

const onAttachmentSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files?.length) {
    attachmentFiles.value = [
      ...attachmentFiles.value,
      ...Array.from(target.files).map(file => ({
        file,
        filename: file.name,
      })),
    ]
  }
  if (attachmentInputRef.value) {
    attachmentInputRef.value.value = ''
  }
}

const removeAttachment = (index: number) => {
  if (isFormReadonly.value) {
    return
  }
  attachmentFiles.value = attachmentFiles.value.filter((_, i) => i !== index)
  if (attachmentFiles.value.length <= ATTACHMENTS_PREVIEW_COUNT) {
    areAllAttachmentsVisible.value = false
  }
}

const removeMainDocument = () => {
  if (isFormReadonly.value) {
    return
  }
  removeFile()
}

const getFormDropScope = (): HTMLElement | null => {
  const el = formCardRef.value?.$el
  return el instanceof HTMLElement ? el : null
}

const needsMainFileSlot = () =>
  !fileInput.value && (!lastVersion.value || existingFileRemoved.value)

const canAddDroppedAttachments = () =>
  isEditMode.value
    ? hasEditMainFile.value || !!fileInput.value
    : !!fileInput.value

/** Как в FileAttach/НС: дроп только кладёт файлы в форму, на сервер — по кнопке сохранения */
const addDroppedFiles = (dropped: File[]) => {
  if (!dropped.length || isFormReadonly.value) {
    return
  }

  let rest = dropped
  if (needsMainFileSlot()) {
    fileInput.value = dropped[0]
    rest = dropped.slice(1)
  }

  if (!rest.length || !canAddDroppedAttachments()) {
    return
  }

  attachmentFiles.value = [
    ...attachmentFiles.value,
    ...rest.map(file => ({ file, filename: file.name })),
  ]
}

const formFileDrop = useFormFileDrop({
  getScope: getFormDropScope,
  isDisabled: () =>
    !props.isOpen
    || isFormReadonly.value
    || (isEditMode.value && isFetchingDetails.value),
  addFiles: addDroppedFiles,
})

type DateFormField = 'approved_at' | 'effective_at' | 'valid_until'

const normalizeFormDate = (value?: string | null): string => {
  if (!value) {
    return ''
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const pad = (part: number) => String(part).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const getDateFieldValue = (field: DateFormField) =>
  formData.value[field] || null

const setDateFieldValue = (field: DateFormField, value: string | null) => {
  if (isFormReadonly.value) {
    return
  }
  formData.value[field] = value ?? ''
}

const openDeveloperModal = async () => {
  if (isFormReadonly.value) {
    return
  }
  suppressDismissTemporarily('developer-modal-open')
  await nextTick()
  employeeModalRef.value?.open()
}

const onDevelopersSelected = (employees: IEmployeeTreeItem[]) => {
  suppressDismissTemporarily('developer-selected')
  selectedDevelopers.value = employees
}

const removeDeveloper = () => {
  if (isFormReadonly.value) {
    return
  }
  selectedDevelopers.value = []
}

const accessParticipantsTotal = computed(
  () => selectedEmployees.value.length + selectedDepartments.value.length + selectedPositions.value.length,
)

const accessSelectedGroups = computed(() => {
  const groups: {
    key: 'employees' | 'departments' | 'positions'
    title: string
    hideAvatar: boolean
    items: { id: string; name: string; description?: string }[]
  }[] = []

  if (selectedEmployees.value.length) {
    groups.push({
      key: 'employees',
      title: 'Сотрудники',
      hideAvatar: false,
      items: selectedEmployees.value.map(item => ({
        id: String(item.id),
        name: item.full_name,
        description: item.position_name,
      })),
    })
  }

  if (selectedDepartments.value.length) {
    groups.push({
      key: 'departments',
      title: 'Подразделения',
      hideAvatar: true,
      items: selectedDepartments.value.map(item => ({
        id: item.id,
        name: item.name,
      })),
    })
  }

  if (selectedPositions.value.length) {
    groups.push({
      key: 'positions',
      title: 'Должности',
      hideAvatar: true,
      items: selectedPositions.value.map(item => ({
        id: item.id,
        name: item.name,
      })),
    })
  }

  return groups
})

const openAccessParticipantsModal = async () => {
  if (isFormReadonly.value) {
    return
  }
  suppressDismissTemporarily('access-participants-modal-open')
  await nextTick()
  participantModalRef.value?.open('employees')
}

const onAccessParticipantsSelected = (payload: IParticipantSelectionResult) => {
  suppressDismissTemporarily('access-participants-selected')
  selectedEmployees.value = payload.employees.map(item => ({
    id: item.id,
    full_name: item.full_name,
    position_name: item.position_name || '',
  }))
  selectedDepartments.value = payload.departments
  selectedPositions.value = payload.positions
}

const removeAccessParticipantItem = (id: string | number, groupKey?: string) => {
  if (isFormReadonly.value) {
    return
  }
  const itemId = String(id)
  if (groupKey === 'employees') {
    selectedEmployees.value = selectedEmployees.value.filter(emp => String(emp.id) !== itemId)
    return
  }
  if (groupKey === 'departments') {
    selectedDepartments.value = selectedDepartments.value.filter(item => item.id !== itemId)
    return
  }
  if (groupKey === 'positions') {
    selectedPositions.value = selectedPositions.value.filter(item => item.id !== itemId)
  }
}

const removeAccessParticipantGroup = (groupKey: string) => {
  if (isFormReadonly.value) {
    return
  }
  if (groupKey === 'employees') {
    selectedEmployees.value = []
    return
  }
  if (groupKey === 'departments') {
    selectedDepartments.value = []
    return
  }
  if (groupKey === 'positions') {
    selectedPositions.value = []
  }
}

const clearAccessParticipants = () => {
  if (isFormReadonly.value) {
    return
  }
  selectedEmployees.value = []
  selectedDepartments.value = []
  selectedPositions.value = []
}

const openFolderModal = () => {
  if (isFormReadonly.value) {
    return
  }
  suppressDismissTemporarily('folder-modal-open')
  folderModalRef.value?.open()
}

const onFolderSelected = (folderId: string | null, folderName: string) => {
  suppressDismissTemporarily('folder-selected')
  formData.value.folder_id = folderId
  selectedFolderName.value = folderName || ''
}

const onSectionsUpdate = (ids: string[]) => {
  if (isFormReadonly.value) {
    return
  }
  suppressDismissTemporarily('sections-selected')
  formData.value.section_ids = ids.filter(id => !rootSectionIds.value.has(id))
}

const closeModal = () => {
  console.log('[DocumentFormSidebar] closeModal called')
  feedbackStore.isGlobalLoading = false
  isFetchingDetails.value = false
  emit('update:isOpen', false)
  resetForm()
}

const onDialogDismissAttemptGuarded = useDialogDismissGuard(closeModal, 'DocumentFormSidebar')

const onDialogDismissAttempt = (eventOrReason: MouseEvent | string = 'unknown') => {
  const now = Date.now()
  if (now < dismissSuppressedUntil.value) {
    console.log('[DocumentFormSidebar] dismiss suppressed by cooldown', {
      remainingMs: dismissSuppressedUntil.value - now,
    })
    return
  }

  if (eventOrReason instanceof Event) {
    const target = eventOrReason.target
    if (target instanceof Element && target.closest('.entity-form-actions')) {
      return
    }
  }

  const reason = typeof eventOrReason === 'string' ? eventOrReason : 'click-outside'
  onDialogDismissAttemptGuarded(reason)
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const formatDateShort = (dateStr?: string | null): string => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '—'
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)

  return `${day}.${month}.${year}`
}

const formatDateTime = (dateStr: string): string => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
}

const buildVersionFileUrl = (file: string): string =>
  file.startsWith('http') ? file : `${BASE_URL}${file}`

const downloadVersionFile = (version: DocumentVersion) => {
  if (!version.file) return

  const a = document.createElement('a')
  a.href = buildVersionFileUrl(version.file)
  a.download = getVersionFileDisplayName(version)
  a.rel = 'noopener'
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

const openMainDocumentFile = () => {
  if (lastVersion.value?.file && !existingFileRemoved.value) {
    downloadVersionFile(lastVersion.value)

    return
  }

  if (fileInput.value) {
    const url = URL.createObjectURL(fileInput.value)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }
}

const onHistoryRowClick = (_event: unknown, { item }: { item: DocumentVersion }) => {
  downloadVersionFile(item)
}

const appendVersionMeta = (fd: FormData) => {
  const edition = formData.value.edition.trim()
  if (edition) {
    fd.append('edition', edition)
  }
  if (formData.value.approved_at) {
    fd.append('approved_at', formData.value.approved_at)
  }
  if (formData.value.valid_until) {
    fd.append('valid_until', formData.value.valid_until)
  }
  if (formData.value.effective_at) {
    fd.append('effective_at', formData.value.effective_at)
  }
}

const appendChangedVersionMeta = (fd: FormData) => {
  const snapshot = initialEditFormSnapshot.value
  if (!snapshot) {
    return
  }

  const current = formData.value
  const initial = snapshot.formData

  if (current.edition.trim() !== initial.edition.trim()) {
    fd.append('edition', current.edition.trim())
  }
  if (current.approved_at !== initial.approved_at) {
    fd.append('approved_at', current.approved_at)
  }
  if (current.valid_until !== initial.valid_until) {
    fd.append('valid_until', current.valid_until)
  }
  if (current.effective_at !== initial.effective_at) {
    fd.append('effective_at', current.effective_at)
  }
}

const hasVersionMetaChanges = (): boolean => {
  const snapshot = initialEditFormSnapshot.value
  if (!snapshot) {
    return false
  }

  const current = formData.value
  const initial = snapshot.formData

  return (
    current.edition.trim() !== initial.edition.trim()
    || current.approved_at !== initial.approved_at
    || current.valid_until !== initial.valid_until
    || current.effective_at !== initial.effective_at
  )
}

const handleDeleteClick = () => {
  if (isFormReadonly.value) {
    return
  }
  isDeleteConfirmOpen.value = true
}

const confirmDeleteDocument = async () => {
  if (!props.documentId) {
    return
  }

  isDeleting.value = true
  try {
    await DocumentsApi.deleteDocuments(props.documentId)
    showSuccess('Документ удален')
    emit('deleted')
    closeModal()
  } catch (error: unknown) {
    showError(error, 'Удаление документа')
  } finally {
    isDeleting.value = false
    isDeleteConfirmOpen.value = false
  }
}

const handleArchiveClick = async () => {
  if (!props.documentId || !canChangeArchiveStatus.value) {
    return
  }

  const currentStatus = documentDetails.value?.status
  const newStatus = getDocumentStatusTransition(currentStatus)
  if (!newStatus) {
    return
  }

  isChangingStatus.value = true
  try {
    await DocumentsApi.changeDocumentStatus(props.documentId, newStatus)
    showSuccess(
      newStatus === 'archived'
        ? 'Документ архивирован'
        : isDraftDocumentStatus(currentStatus)
          ? 'Документ активирован'
          : 'Документ восстановлен',
    )
    await loadDocumentData()
    emit('updated')
  } catch (error: unknown) {
    showError(error, 'Изменение статуса документа')
  } finally {
    isChangingStatus.value = false
  }
}

const handleRevisionAdded = async () => {
  await loadDocumentData()
  emit('updated')
}

const onSubmit = async () => {
  if (isFormReadonly.value) {
    return
  }
  if (!isEditMode.value && !hasMainDocument.value) {
    return
  }

  if (isEditingName.value) {
    saveName()
  }

  if (isEditMode.value && !formData.value.name?.trim()) {
    showWarning('Наименование обязательно')
    return
  }

  const { valid } = await formRef.value.validate()
  if (!valid) {
    return
  }

  if (isEditMode.value && !props.documentId) {
    return
  }

  isSubmitting.value = true
  try {
    if (isEditMode.value) {
      const payload: UpdateDocumentPayload = {
        number: formData.value.number || undefined,
        name: formData.value.name,
        author_ids: selectedDeveloperIds.value,
        global_access: isAvailableToAll.value,
        department_access: isAvailableToAll.value
          ? []
          : selectedDepartments.value.map(dep => ({ id: dep.id })),
        position_access: isAvailableToAll.value
          ? []
          : selectedPositions.value.map(pos => ({ id: pos.id })),
        employee_access: isAvailableToAll.value
          ? []
          : selectedEmployees.value.map(emp => ({ id: String(emp.id) })),
        type_id: formData.value.type_id!,
        folder_id: formData.value.folder_id ?? undefined,
        sections: formData.value.section_ids.map(id => ({ id })),
      }

      await DocumentsApi.updateDocument(props.documentId!, payload)

      const isReplacingFile = existingFileRemoved.value && !!fileInput.value

      if (isReplacingFile) {
        const fd = new FormData()

        fd.append('file', fileInput.value!)
        appendVersionMeta(fd)
        await DocumentsApi.addDocumentVersion(props.documentId!, fd)
      } else if (hasVersionMetaChanges()) {
        const fd = new FormData()

        appendChangedVersionMeta(fd)
        await DocumentsApi.updateDocumentLastVersion(props.documentId!, fd)
      }

      showSuccess('Документ успешно обновлен')
      emit('updated')
    } else {
      const payload: CreateDocumentPayload = {
        number: formData.value.number || '',
        name: formData.value.name,
        author_ids: selectedDeveloperIds.value,
        sections: formData.value.section_ids.map(id => ({ id })),
        global_access: isAvailableToAll.value,
        department_access: isAvailableToAll.value
          ? []
          : selectedDepartments.value.map(dep => ({ id: dep.id })),
        position_access: isAvailableToAll.value
          ? []
          : selectedPositions.value.map(pos => ({ id: pos.id })),
        employee_access: isAvailableToAll.value
          ? []
          : selectedEmployees.value.map(emp => ({ id: String(emp.id) })),
        type_id: formData.value.type_id!,
        folder_id: formData.value.folder_id!,
      }

      const doc = await DocumentsApi.createDocument(payload)

      const fd = new FormData()

      fd.append('file', fileInput.value!)
      appendVersionMeta(fd)
      attachmentFiles.value.forEach(({ file }) => fd.append('applications', file))
      await DocumentsApi.addDocumentVersion(doc.id, fd)
      showSuccess('Документ успешно создан')
      emit('created', formData.value.folder_id)
    }

    closeModal()
  } catch (error: unknown) {
    console.error(
      `Ошибка при ${isEditMode.value ? 'обновлении' : 'создании'} документа:`,
      error,
    )
    showError(
      error,
      isEditMode.value ? 'Обновление документа' : 'Создание документа',
    )
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="formEmbeddedLayout.isEmbedded.value && isOpen"
      class="entity-form-backdrop"
    />
  </Teleport>
  <Teleport
    v-if="formEmbeddedLayout.isEmbedded.value && isOpen"
    to="body"
  >
    <div
      class="entity-form-actions"
      @click.stop
      @mousedown.stop
      @pointerdown.stop
    >
      <VTooltip
        v-if="isEditMode"
        location="bottom"
        text="Лист ознакомления"
      >
        <template #activator="{ props: tooltipProps }">
          <VBtn
            v-bind="tooltipProps"
            icon
            color="primary"
            class="entity-form-action-btn"
            aria-label="Лист ознакомления"
            @click.stop="suppressDismissTemporarily('acknowledgement-btn'); toggleAcknowledgementPane()"
          >
            <span class="history-toggle-icon">
              <VIcon icon="ri-file-user-line" />
              <span
                v-if="isAcknowledgementOpen"
                class="history-toggle-slash"
                aria-hidden="true"
              />
            </span>
          </VBtn>
        </template>
      </VTooltip>
      <VTooltip
        v-if="isEditMode"
        location="bottom"
        text="Тексты документа"
      >
        <template #activator="{ props: tooltipProps }">
          <VBtn
            v-bind="tooltipProps"
            icon
            color="primary"
            class="entity-form-action-btn"
            aria-label="Тексты документа"
            @click.stop="suppressDismissTemporarily('texts-btn'); toggleTextsPane()"
          >
            <span class="history-toggle-icon">
              <VIcon icon="ri-file-text-line" />
              <span
                v-if="isTextsOpen"
                class="history-toggle-slash"
                aria-hidden="true"
              />
            </span>
          </VBtn>
        </template>
      </VTooltip>
      <VTooltip
        v-if="isEditMode"
        location="bottom"
        text="История редакций"
      >
        <template #activator="{ props: tooltipProps }">
          <VBtn
            v-bind="tooltipProps"
            icon
            color="primary"
            class="entity-form-action-btn"
            aria-label="История редакций"
            @click.stop="suppressDismissTemporarily('history-btn'); toggleHistoryPane()"
          >
            <span class="history-toggle-icon">
              <VIcon icon="ri-file-history-line" />
              <span
                v-if="isHistoryOpen"
                class="history-toggle-slash"
                aria-hidden="true"
              />
            </span>
          </VBtn>
        </template>
      </VTooltip>
      <CopyEntityLink
        v-if="shareDocumentTo"
        variant="ear"
        :to="shareDocumentTo"
      />
      <VTooltip
        location="bottom"
        text="Закрыть"
      >
        <template #activator="{ props: tooltipProps }">
          <VBtn
            v-bind="tooltipProps"
            icon="ri-close-line"
            color="primary"
            class="entity-form-action-btn"
            aria-label="Закрыть"
            @click="closeModal"
          />
        </template>
      </VTooltip>
    </div>
  </Teleport>

  <VDialog
    :model-value="isOpen"
    eager
    :max-width="dialogMaxWidth"
    :content-class="dialogContentClass"
    :scrim="!formEmbeddedLayout.isEmbedded.value"
    persistent
    @click:outside="onDialogDismissAttempt($event)"
    @keydown.esc.stop="onDialogDismissAttempt('esc-key')"
  >
    <FileDropLayer :rect="formFileDrop.rect.value" />
    <VCard
      ref="formCardRef"
      class="document-form-card"
      :class="{
        'document-form-card--create': !isEditMode,
        'document-form-card--edit': isEditMode,
        'entity-form-card h-100': formEmbeddedLayout.isEmbedded.value,
      }"
      variant="elevated"
      :loading="isEditMode && isFetchingDetails"
      :disabled="isEditMode && isFetchingDetails"
    >
      <div
        :class="{
          'entity-form-body d-flex h-100 overflow-hidden': formEmbeddedLayout.isEmbedded.value,
          'document-form-body document-form-body--modal': isEditMode && !formEmbeddedLayout.isEmbedded.value,
        }"
      >
        <div
          :class="isEditMode
            ? (formEmbeddedLayout.isEmbedded.value ? 'entity-form-pane flex-grow-1 min-h-0' : 'document-form-pane')
            : undefined"
        >
          <VCardTitle
            class="document-form-header d-flex flex-column gap-2 flex-shrink-0"
            :class="{
              'px-6 pt-6 pb-4': !xs,
              'px-4 py-3': xs,
              'pb-2': isEditMode && !formEmbeddedLayout.isEmbedded.value && xs,
              'document-form-header--side-open': isModalSideOpen && xs,
            }"
          >
            <!-- Мобилка: кнопки над заголовком, как в карточках НС / заявок -->
            <div
              v-if="isEditMode && !formEmbeddedLayout.isEmbedded.value && xs"
              class="d-flex align-center justify-space-evenly w-100"
              :class="{ 'mb-3': !isSidePaneOpen }"
            >
              <VBtn
                icon
                variant="text"
                color="primary"
                class="document-header-action-btn"
                :class="{ 'document-header-action-btn--active': isAcknowledgementOpen }"
                aria-label="Лист ознакомления"
                @click="toggleAcknowledgementPane()"
              >
                <span class="history-toggle-icon">
                  <VIcon icon="ri-file-user-line" />
                  <span
                    v-if="isAcknowledgementOpen"
                    class="history-toggle-slash"
                    aria-hidden="true"
                  />
                </span>
              </VBtn>
              <VBtn
                icon
                variant="text"
                color="primary"
                class="document-header-action-btn"
                :class="{ 'document-header-action-btn--active': isTextsOpen }"
                aria-label="Тексты документа"
                @click="toggleTextsPane()"
              >
                <span class="history-toggle-icon">
                  <VIcon icon="ri-file-text-line" />
                  <span
                    v-if="isTextsOpen"
                    class="history-toggle-slash"
                    aria-hidden="true"
                  />
                </span>
              </VBtn>
              <VBtn
                icon
                variant="text"
                color="primary"
                class="document-header-action-btn"
                :class="{ 'document-header-action-btn--active': isHistoryOpen }"
                aria-label="История редакций"
                @click="toggleHistoryPane()"
              >
                <span class="history-toggle-icon">
                  <VIcon icon="ri-file-history-line" />
                  <span
                    v-if="isHistoryOpen"
                    class="history-toggle-slash"
                    aria-hidden="true"
                  />
                </span>
              </VBtn>
              <CopyEntityLink
                v-if="shareDocumentTo"
                variant="inline"
                :to="shareDocumentTo"
              />
              <CustomCloseButton :close-action="closeModal" />
            </div>

            <template v-if="!isModalSideOpen">
            <div
              class="form_card d-flex w-100 align-center gap-3 justify-space-between"
            >
              <div class="document-form-header__title-wrap d-flex align-center ga-2 min-w-0 flex-grow-1">
                <template v-if="!isEditMode">
                  <span class="document-form-header__title text-h5 font-weight-medium">
                    Добавление документа
                  </span>
                </template>
                <template v-else-if="isEditingName">
                  <VTextField
                    v-model="nameInput"
                    density="compact"
                    variant="underlined"
                    autofocus
                    hide-details
                    class="document-title-inline-input flex-grow-1"
                    @keydown.enter.prevent="saveName"
                    @keydown.esc.prevent="cancelEditName"
                    @blur="saveName"
                  />
                </template>
                <VTooltip
                  v-else
                  :disabled="!canEditName"
                  location="top"
                  text="Нажмите для редактирования"
                >
                  <template #activator="{ props: tipProps }">
                    <div
                      v-bind="tipProps"
                      class="document-form-header__name"
                      :class="{ 'cursor-pointer hover-editable-text': canEditName }"
                      @click="canEditName ? startEditName() : undefined"
                    >
                      {{ documentHeaderName || 'Без названия' }}
                    </div>
                  </template>
                </VTooltip>
              </div>

              <div
                v-if="!formEmbeddedLayout.isEmbedded.value && (!xs || !isEditMode)"
                class="d-flex align-center gap-2 flex-shrink-0"
              >
                <CopyEntityLink
                  v-if="shareDocumentTo && !xs"
                  variant="inline"
                  :to="shareDocumentTo"
                />
                <template v-if="isEditMode">
                  <VChip
                    v-if="statusChipConfig && documentDetails?.status"
                    size="small"
                    variant="flat"
                    class="document-status-chip"
                    :style="{
                      backgroundColor: statusChipConfig.bgColor,
                      color: statusChipConfig.textColor,
                    }"
                  >
                    {{ getDocumentStatusLabel(documentDetails.status) }}
                  </VChip>
                  <VChip
                    v-if="versionChipLabel"
                    size="small"
                    variant="flat"
                    class="document-version-chip document-version-chip--current"
                  >
                    {{ versionChipLabel }}
                  </VChip>
                  <VBtn
                    icon
                    variant="text"
                    color="secondary"
                    class="close-icon-btn hover-primary"
                    aria-label="Лист ознакомления"
                    @click="toggleAcknowledgementPane()"
                  >
                    <span class="history-toggle-icon">
                      <VIcon icon="ri-file-user-line" />
                      <span
                        v-if="isAcknowledgementOpen"
                        class="history-toggle-slash"
                        aria-hidden="true"
                      />
                    </span>
                  </VBtn>
                  <VBtn
                    icon
                    variant="text"
                    color="secondary"
                    class="close-icon-btn hover-primary"
                    aria-label="Тексты документа"
                    @click="toggleTextsPane()"
                  >
                    <span class="history-toggle-icon">
                      <VIcon icon="ri-file-text-line" />
                      <span
                        v-if="isTextsOpen"
                        class="history-toggle-slash"
                        aria-hidden="true"
                      />
                    </span>
                  </VBtn>
                  <VBtn
                    icon
                    variant="text"
                    color="secondary"
                    class="close-icon-btn hover-primary"
                    aria-label="История редакций"
                    @click="toggleHistoryPane()"
                  >
                    <span class="history-toggle-icon">
                      <VIcon icon="ri-file-history-line" />
                      <span
                        v-if="isHistoryOpen"
                        class="history-toggle-slash"
                        aria-hidden="true"
                      />
                    </span>
                  </VBtn>
                </template>
                <CustomCloseButton :close-action="closeModal" />
              </div>

              <div
                v-else-if="isEditMode && xs"
                class="d-flex align-center gap-2 flex-shrink-0"
              >
                <VChip
                  v-if="statusChipConfig && documentDetails?.status"
                  size="small"
                  variant="flat"
                  class="document-status-chip"
                  :style="{
                    backgroundColor: statusChipConfig.bgColor,
                    color: statusChipConfig.textColor,
                  }"
                >
                  {{ getDocumentStatusLabel(documentDetails.status) }}
                </VChip>
                <VChip
                  v-if="versionChipLabel"
                  size="small"
                  variant="flat"
                  class="document-version-chip document-version-chip--current"
                >
                  {{ versionChipLabel }}
                </VChip>
              </div>
            </div>

            <div
              v-if="isEditMode && folderBreadcrumbs.length"
              class="folder-breadcrumbs text-caption text-medium-emphasis"
            >
              {{ folderBreadcrumbs.join(" / ") }}
            </div>
            </template>
          </VCardTitle>

          <div
            ref="documentFormMainRef"
            class="document-form-main"
          >
          <VCardText
            v-if="!isModalSideOpen"
            class="d-flex flex-column document-form-card-text"
            :class="{
              'flex-grow-1 min-h-0': formEmbeddedLayout.isEmbedded.value || isEditMode,
              'document-form-card-text--edit': isEditMode,
            }"
          >
            <VForm
              ref="formRef"
              class="document-form d-flex flex-column"
              :class="[
                (formEmbeddedLayout.isEmbedded.value || isEditMode) ? 'flex-grow-1 min-h-0' : '',
                !isEditMode ? 'document-form--create' : '',
              ]"
              @submit.prevent="onSubmit"
            >
              <div :class="isEditMode ? 'entity-form-scroll-body' : undefined">
                <VCol class="d-flex flex-column gap-4 px-0">
                  <template v-if="isEditMode">
                    <VTextField
                      v-model="formData.edition"
                      label="Редакция"
                      density="compact"
                      variant="outlined"
                      :maxlength="DOCUMENT_EDITION_MAX_LENGTH"
                      :readonly="isFormReadonly"
                      hide-details
                    />

                    <div class="form-row-two-cols">
                      <VTextField
                        v-model="formData.number"
                        label="Номер"
                        density="compact"
                        variant="outlined"
                        :readonly="isFormReadonly"
                        hide-details
                      />

                      <DateMaskInput
                        density="compact"
                        :disabled="isFormReadonly"
                        :model-value="getDateFieldValue('approved_at')"
                        @update:model-value="setDateFieldValue('approved_at', $event)"
                      >
                        <template #label>
                          <span>Дата утверждения</span>
                        </template>
                      </DateMaskInput>
                    </div>

                    <VTextField
                      :model-value="selectedDevelopersDisplay"
                      label="Разработчики"
                      density="compact"
                      variant="outlined"
                      readonly
                      :clearable="!isFormReadonly"
                      :append-inner-icon="isFormReadonly ? undefined : 'ri-arrow-down-s-line'"
                      hide-details
                      :class="{ 'cursor-pointer invisible-readonly': !isFormReadonly }"
                      @click="openDeveloperModal"
                      @click:append-inner="openDeveloperModal"
                      @click:clear="removeDeveloper"
                    />

                    <div class="form-row-two-cols">
                      <VSelect
                        v-model="formData.type_id"
                        label="Вид"
                        density="compact"
                        variant="outlined"
                        :items="sortedDocumentTypesOptions"
                        item-title="title"
                        item-value="value"
                        :loading="isFetchingTypes"
                        :readonly="isFormReadonly"
                        :disabled="isFormReadonly"
                        :menu-icon="isFormReadonly ? '' : undefined"
                        :rules="isFormReadonly ? [] : rules.type_id"
                        hide-details="auto"
                      />

                      <VTextField
                        :model-value="selectedFolderName"
                        label="Папка"
                        density="compact"
                        variant="outlined"
                        :readonly="isFormReadonly"
                        :append-inner-icon="isFormReadonly ? undefined : 'ri-arrow-down-s-line'"
                        :rules="isFormReadonly ? [] : rules.folder_id"
                        validate-on="submit"
                        hide-details="auto"
                        :class="{ 'cursor-pointer': !isFormReadonly }"
                        @click="openFolderModal"
                        @click:append-inner="openFolderModal"
                      />
                    </div>

                    <CustomDivider title="Файлы" />

                    <template v-if="!hasEditMainFile">
                      <VBtn
                        v-if="!isFormReadonly"
                        variant="outlined"
                        color="primary"
                        prepend-icon="ri-upload-2-line"
                        class="text-none upload-file-btn"
                        block
                        @click="openFileDialog"
                      >
                        Загрузить файл
                      </VBtn>
                      <input
                        v-if="!isFormReadonly"
                        ref="fileInputRef"
                        type="file"
                        style="display: none"
                        @change="onFileSelect"
                      >
                    </template>

                    <template v-else>
                      <div
                        v-if="lastVersion && !existingFileRemoved"
                        class="file-preview main-document-preview"
                      >
                        <div
                          class="d-flex align-center gap-2 pa-3 file-card file-card--openable"
                          role="button"
                          tabindex="0"
                          @click="openMainDocumentFile"
                          @keydown.enter.prevent="openMainDocumentFile"
                        >
                          <VIcon
                            :icon="getFileIcon(getVersionFileDisplayName(lastVersion))"
                            size="24"
                          />
                          <div class="flex-grow-1 file-card-content">
                            <div class="text-body-2 font-weight-medium file-card-name">
                              {{ getVersionFileDisplayName(lastVersion) }}
                            </div>
                            <div
                              v-if="lastVersion.size"
                              class="text-caption text-medium-emphasis"
                            >
                              {{ formatFileSize(lastVersion.size) }}
                            </div>
                          </div>
                          <VIconBtn
                            icon="ri-external-link-line"
                            variant="text"
                            size="small"
                            aria-label="Открыть файл"
                            @click.stop="openMainDocumentFile"
                          />
                          <VIconBtn
                            v-if="!isFormReadonly"
                            icon="ri-close-line"
                            variant="text"
                            size="small"
                            class="main-document-remove-btn"
                            aria-label="Удалить файл (документ)"
                            @click.stop="existingFileRemoved = true"
                          />
                        </div>
                      </div>

                      <div
                        v-else-if="fileInput"
                        class="file-preview main-document-preview"
                      >
                        <div
                          class="d-flex align-center gap-2 pa-3 file-card file-card--openable"
                          role="button"
                          tabindex="0"
                          @click="openMainDocumentFile"
                          @keydown.enter.prevent="openMainDocumentFile"
                        >
                          <VIcon
                            :icon="getFileIcon(fileInput.name)"
                            size="24"
                          />
                          <div class="flex-grow-1 file-card-content">
                            <div class="text-body-2 font-weight-medium file-card-name">
                              {{ fileInput.name }}
                            </div>
                            <div class="text-caption text-medium-emphasis">
                              {{ formatFileSize(fileInput.size) }}
                            </div>
                          </div>
                          <VIconBtn
                            icon="ri-external-link-line"
                            variant="text"
                            size="small"
                            aria-label="Открыть файл"
                            @click.stop="openMainDocumentFile"
                          />
                          <VIconBtn
                            v-if="!isFormReadonly"
                            icon="ri-close-line"
                            variant="text"
                            size="small"
                            class="main-document-remove-btn"
                            aria-label="Удалить файл (документ)"
                            @click.stop="removeMainDocument"
                          />
                        </div>
                      </div>

                      <input
                        ref="fileInputRef"
                        type="file"
                        style="display: none"
                        @change="onFileSelect"
                      >

                      <DocumentAudioPlayer
                        v-if="props.isOpen && props.documentId && lastVersion && !existingFileRemoved"
                        :key="`${props.documentId}-${lastVersion.id}`"
                        :document-id="props.documentId"
                        class="mt-2"
                      />

                      <!-- TODO: Добавить приложения -->
                      <!--
                        <VBtn
                        variant="outlined"
                        color="secondary"
                        class="text-none add-attachments-btn"
                        block
                        @click="openAttachmentDialog"
                        >
                        <span class="add-attachments-btn__icon">
                        <VIcon
                        icon="ri-add-line"
                        size="14"
                        />
                        </span>
                        Добавить приложения
                        </VBtn>
                        <input
                        ref="attachmentInputRef"
                        type="file"
                        multiple
                        style="display: none"
                        @change="onAttachmentSelect"
                        >
                      -->
                    </template>

                    <div
                      v-if="attachmentFiles.length"
                      class="attachments-section"
                    >
                      <div class="attachments-grid">
                        <div
                          v-for="{ item: attachment, index } in visibleAttachmentEntries"
                          :key="`${attachment.filename}-${attachment.file.size}-${index}`"
                          class="file-preview"
                        >
                          <div class="d-flex align-center gap-2 pa-3 file-card">
                            <VIcon
                              :icon="getFileIcon(attachment.filename)"
                              size="24"
                            />
                            <div class="flex-grow-1 file-card-content">
                              <div class="text-body-2 font-weight-medium file-card-name">
                                {{ attachment.filename }}
                              </div>
                              <div class="text-caption text-medium-emphasis">
                                {{ formatFileSize(attachment.file.size) }}
                              </div>
                            </div>
                            <VIconBtn
                              v-if="!isFormReadonly"
                              icon="ri-close-line"
                              variant="text"
                              size="small"
                              class="main-document-remove-btn"
                              aria-label="Удалить приложение"
                              @click="removeAttachment(index)"
                            />
                          </div>
                        </div>
                      </div>

                      <button
                        v-if="hasHiddenAttachments"
                        type="button"
                        class="attachments-show-all"
                        @click="areAllAttachmentsVisible = true"
                      >
                        Все
                        <VIcon
                          icon="ri-arrow-down-s-line"
                          size="18"
                        />
                      </button>
                    </div>

                    <CustomDivider title="Доступ" />

                    <VRadioGroup
                      v-model="accessType"
                      inline
                      color="primary"
                      density="compact"
                      hide-details
                      class="access-radio-group"
                      :disabled="isFormReadonly"
                      :readonly="isFormReadonly"
                    >
                      <VRadio
                        label="Доступен всем"
                        value="all"
                      />
                      <VRadio
                        label="Ограниченный доступ"
                        value="limited"
                      />
                    </VRadioGroup>

                    <div
                      v-if="!isAvailableToAll"
                      class="limited-access-panel d-flex flex-column gap-3"
                    >
                      <VBtn
                        v-if="!isFormReadonly"
                        variant="outlined"
                        color="primary"
                        prepend-icon="ri-user-add-line"
                        block
                        class="text-none"
                        @click="openAccessParticipantsModal"
                      >
                        Выбрать участников для доступа
                      </VBtn>
                      <div v-if="accessParticipantsTotal">
                        <VMenu
                          open-on-hover
                          :close-on-content-click="false"
                          close-delay="200"
                          location="bottom start"
                          offset="8"
                          content-class="date-picker-menu"
                        >
                          <template #activator="{ props: menuProps }">
                            <span
                              v-bind="menuProps"
                              class="text-body-2 text-medium-emphasis cursor-pointer d-inline-block"
                            >
                              Выбрано: {{ accessParticipantsTotal }}
                            </span>
                          </template>

                          <CustomSelectedItems
                            :groups="accessSelectedGroups"
                            :title="`Выбрано ${accessParticipantsTotal}:`"
                            :readonly="isFormReadonly"
                            @remove="removeAccessParticipantItem"
                            @remove-all="clearAccessParticipants"
                            @remove-group="removeAccessParticipantGroup"
                          />
                        </VMenu>
                      </div>
                    </div>

                    <CustomDivider title="Дополнительная информация" />

                    <div @click="suppressDismissTemporarily('rzn-select')">
                      <NewTreeMultiSelectComponent
                        label="Разделы нормативов"
                        placeholder="Выберите разделы нормативов"
                        search-placeholder="Поиск по разделам..."
                        :tree-data="rznSectionsTreeData"
                        :selected="formData.section_ids"
                        :disabled="isFetchingSections || isFormReadonly"
                        @update:selected="onSectionsUpdate"
                      />
                    </div>

                    <div class="form-row-two-cols">
                      <DateMaskInput
                        density="compact"
                        :disabled="isFormReadonly"
                        :model-value="getDateFieldValue('effective_at')"
                        @update:model-value="setDateFieldValue('effective_at', $event)"
                      >
                        <template #label>
                          <span>Дата ввода</span>
                        </template>
                      </DateMaskInput>
                      <DateMaskInput
                        density="compact"
                        :disabled="isFormReadonly"
                        :model-value="getDateFieldValue('valid_until')"
                        @update:model-value="setDateFieldValue('valid_until', $event)"
                      >
                        <template #label>
                          <span>Дата истечения</span>
                        </template>
                      </DateMaskInput>
                    </div>
                  </template>

                  <template v-else>
                    <VTextField
                      v-model="formData.edition"
                      label="Редакция"
                      density="compact"
                      variant="outlined"
                      :maxlength="DOCUMENT_EDITION_MAX_LENGTH"
                      hide-details
                    />

                    <div class="form-row-two-cols">
                      <VTextField
                        v-model="formData.number"
                        label="Номер"
                        density="compact"
                        variant="outlined"
                        hide-details
                      />

                      <DateMaskInput
                        density="compact"
                        :model-value="getDateFieldValue('approved_at')"
                        @update:model-value="setDateFieldValue('approved_at', $event)"
                      >
                        <template #label>
                          <span>Дата утверждения</span>
                        </template>
                      </DateMaskInput>
                    </div>

                    <VTextarea
                      v-model="formData.name"
                      label="Наименование"
                      density="compact"
                      variant="outlined"
                      :rules="rules.name"
                      hide-details="auto"
                      rows="1"
                      auto-grow
                      no-resize
                    />

                    <VTextField
                      :model-value="selectedDevelopersDisplay"
                      label="Разработчики"
                      density="compact"
                      variant="outlined"
                      readonly
                      clearable
                      append-inner-icon="ri-arrow-down-s-line"
                      hide-details
                      class="cursor-pointer invisible-readonly"
                      @click="openDeveloperModal"
                      @click:append-inner="openDeveloperModal"
                      @click:clear="removeDeveloper"
                    />

                    <div class="form-row-two-cols">
                      <VSelect
                        v-model="formData.type_id"
                        label="Вид"
                        density="compact"
                        variant="outlined"
                        :items="sortedDocumentTypesOptions"
                        item-title="title"
                        item-value="value"
                        :loading="isFetchingTypes"
                        :rules="rules.type_id"
                        hide-details="auto"
                      />

                      <VTextField
                        :model-value="selectedFolderName"
                        label="Папка"
                        density="compact"
                        variant="outlined"
                        append-inner-icon="ri-arrow-down-s-line"
                        :rules="rules.folder_id"
                        validate-on="submit"
                        hide-details="auto"
                        @click="openFolderModal"
                        @click:append-inner="openFolderModal"
                      />
                    </div>

                    <template v-if="!hasMainDocument">
                      <VBtn
                        variant="outlined"
                        color="primary"
                        prepend-icon="ri-upload-2-line"
                        class="text-none upload-file-btn"
                        block
                        @click="openFileDialog"
                      >
                        Загрузить файл
                      </VBtn>
                      <input
                        ref="fileInputRef"
                        type="file"
                        style="display: none"
                        @change="onFileSelect"
                      >
                      <VTextField
                        :model-value="fileInput ? fileInput.name : ''"
                        class="file-validation-field"
                        :rules="rules.file"
                        validate-on="submit"
                        hide-details="auto"
                      />
                    </template>

                    <template v-else>
                      <div class="file-preview main-document-preview">
                        <div
                          class="d-flex align-center gap-2 pa-3 file-card file-card--openable"
                          role="button"
                          tabindex="0"
                          @click="openMainDocumentFile"
                          @keydown.enter.prevent="openMainDocumentFile"
                        >
                          <VIcon
                            :icon="getFileIcon(fileInput!.name)"
                            size="24"
                          />
                          <div class="flex-grow-1 file-card-content">
                            <div class="text-body-2 font-weight-medium file-card-name">
                              {{ fileInput!.name }}
                            </div>
                            <div class="text-caption text-medium-emphasis">
                              {{ formatFileSize(fileInput!.size) }}
                            </div>
                          </div>
                          <VIconBtn
                            icon="ri-external-link-line"
                            variant="text"
                            size="small"
                            aria-label="Открыть файл"
                            @click.stop="openMainDocumentFile"
                          />
                          <VIconBtn
                            icon="ri-close-line"
                            variant="text"
                            size="small"
                            class="main-document-remove-btn"
                            aria-label="Удалить файл (документ)"
                            @click.stop="removeMainDocument"
                          />
                        </div>
                      </div>

                      <input
                        ref="fileInputRef"
                        type="file"
                        style="display: none"
                        @change="onFileSelect"
                      >

                      <!-- TODO: Добавить приложения -->
                      <!--
                        <VBtn
                        variant="outlined"
                        color="secondary"
                        class="text-none add-attachments-btn"
                        block
                        @click="openAttachmentDialog"
                        >
                        <span class="add-attachments-btn__icon">
                        <VIcon
                        icon="ri-add-line"
                        size="14"
                        />
                        </span>
                        Добавить приложения
                        </VBtn>
                        <input
                        ref="attachmentInputRef"
                        type="file"
                        multiple
                        style="display: none"
                        @change="onAttachmentSelect"
                        >
                      -->
                    </template>

                    <div
                      v-if="attachmentFiles.length"
                      class="attachments-section"
                    >
                      <div class="attachments-grid">
                        <div
                          v-for="{ item: attachment, index } in visibleAttachmentEntries"
                          :key="`${attachment.filename}-${attachment.file.size}-${index}`"
                          class="file-preview"
                        >
                          <div class="d-flex align-center gap-2 pa-3 file-card">
                            <VIcon
                              :icon="getFileIcon(attachment.filename)"
                              size="24"
                            />
                            <div class="flex-grow-1 file-card-content">
                              <div class="text-body-2 font-weight-medium file-card-name">
                                {{ attachment.filename }}
                              </div>
                              <div class="text-caption text-medium-emphasis">
                                {{ formatFileSize(attachment.file.size) }}
                              </div>
                            </div>
                            <VIconBtn
                              v-if="!isFormReadonly"
                              icon="ri-close-line"
                              variant="text"
                              size="small"
                              class="main-document-remove-btn"
                              aria-label="Удалить приложение"
                              @click="removeAttachment(index)"
                            />
                          </div>
                        </div>
                      </div>

                      <button
                        v-if="hasHiddenAttachments"
                        type="button"
                        class="attachments-show-all"
                        @click="areAllAttachmentsVisible = true"
                      >
                        Все
                        <VIcon
                          icon="ri-arrow-down-s-line"
                          size="18"
                        />
                      </button>
                    </div>

                    <VRadioGroup
                      v-model="accessType"
                      inline
                      color="primary"
                      density="compact"
                      hide-details
                      class="access-radio-group"
                    >
                      <VRadio
                        label="Доступен всем"
                        value="all"
                      />
                      <VRadio
                        label="Ограниченный доступ"
                        value="limited"
                      />
                    </VRadioGroup>

                    <div
                      v-if="!isAvailableToAll"
                      class="limited-access-panel d-flex flex-column gap-3"
                    >
                      <VBtn
                        variant="outlined"
                        color="primary"
                        prepend-icon="ri-user-add-line"
                        block
                        class="text-none"
                        @click="openAccessParticipantsModal"
                      >
                        Выбрать участников для доступа
                      </VBtn>
                      <div v-if="accessParticipantsTotal">
                        <VMenu
                          open-on-hover
                          :close-on-content-click="false"
                          close-delay="200"
                          location="bottom start"
                          offset="8"
                          content-class="date-picker-menu"
                        >
                          <template #activator="{ props: menuProps }">
                            <span
                              v-bind="menuProps"
                              class="text-body-2 text-medium-emphasis cursor-pointer d-inline-block"
                            >
                              Выбрано: {{ accessParticipantsTotal }}
                            </span>
                          </template>

                          <CustomSelectedItems
                            :groups="accessSelectedGroups"
                            :title="`Выбрано ${accessParticipantsTotal}:`"
                            @remove="removeAccessParticipantItem"
                            @remove-all="clearAccessParticipants"
                            @remove-group="removeAccessParticipantGroup"
                          />
                        </VMenu>
                      </div>
                    </div>

                    <button
                      type="button"
                      class="additional-info-toggle"
                      :aria-expanded="isAdditionalInfoExpanded"
                      @click="isAdditionalInfoExpanded = !isAdditionalInfoExpanded"
                    >
                      <span>Дополнительная информация</span>
                      <VIcon
                        :icon="isAdditionalInfoExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                        size="18"
                      />
                    </button>

                    <VExpandTransition>
                      <div
                        v-if="isAdditionalInfoExpanded"
                        class="additional-info-panel d-flex flex-column gap-4"
                      >
                        <div @click="suppressDismissTemporarily('rzn-select')">
                          <NewTreeMultiSelectComponent
                            label="Разделы нормативов"
                            placeholder="Выберите разделы нормативов"
                            search-placeholder="Поиск по разделам..."
                            :tree-data="rznSectionsTreeData"
                            :selected="formData.section_ids"
                            :disabled="isFetchingSections"
                            @update:selected="onSectionsUpdate"
                          />
                        </div>

                        <div class="form-row-two-cols">
                          <DateMaskInput
                            density="compact"
                            :model-value="getDateFieldValue('effective_at')"
                            @update:model-value="setDateFieldValue('effective_at', $event)"
                          >
                            <template #label>
                              <span>Дата ввода</span>
                            </template>
                          </DateMaskInput>
                          <DateMaskInput
                            density="compact"
                            :model-value="getDateFieldValue('valid_until')"
                            @update:model-value="setDateFieldValue('valid_until', $event)"
                          >
                            <template #label>
                              <span>Дата истечения</span>
                            </template>
                          </DateMaskInput>
                        </div>
                      </div>
                    </VExpandTransition>
                  </template>
                </VCol>
              </div>

              <footer
                v-if="!isFormReadonly"
                class="document-form-footer flex-shrink-0"
                :class="isEditMode
                  ? 'document-form-footer--edit d-flex align-center py-4 px-6'
                  : 'd-flex justify-end pb-6 pt-2 px-6'"
              >
                <div
                  v-if="isEditMode"
                  class="document-form-footer__actions d-flex gap-2"
                >
                  <VTooltip text="Удалить">
                    <template #activator="{ props: tooltipProps }">
                      <VBtn
                        v-bind="tooltipProps"
                        icon="ri-delete-bin-3-line"
                        variant="tonal"
                        class="document-form-icon-btn document-form-icon-btn--delete"
                        :disabled="!canEdit || isDeleting"
                        aria-label="Удалить"
                        @click="handleDeleteClick"
                      />
                    </template>
                  </VTooltip>
                  <VTooltip :text="archiveTooltip">
                    <template #activator="{ props: tooltipProps }">
                      <VBtn
                        v-bind="tooltipProps"
                        icon="ri-inbox-archive-line"
                        variant="tonal"
                        color="secondary"
                        class="document-form-icon-btn"
                        :disabled="!canChangeArchiveStatus"
                        :loading="isChangingStatus"
                        aria-label="Архивировать"
                        @click="handleArchiveClick"
                      />
                    </template>
                  </VTooltip>
                </div>

                <VBtn
                  color="primary"
                  class="text-none"
                  :class="{ 'ms-auto': isEditMode }"
                  :loading="isSubmitting"
                  :disabled="isSubmitDisabled"
                  @click="onSubmit"
                >
                  {{ submitButtonText }}
                </VBtn>
              </footer>
            </VForm>
          </VCardText>
          </div>
        </div>

        <Teleport
          defer
          :disabled="!isModalSideOpen"
          :to="documentFormMainRef || 'body'"
        >
          <Transition
            :name="isModalSideOpen ? 'document-side-pane' : undefined"
            :css="isModalSideOpen"
          >
            <div
              v-if="isSidePaneOpen && isEditMode"
              :key="isModalSideOpen ? (isTextsOpen ? 'texts' : isHistoryOpen ? 'history' : 'acknowledgement') : 'desktop-side-pane'"
              class="document-history-pane"
              :class="{ 'document-history-pane--fill': isModalSideOpen }"
            >
              <template v-if="isTextsOpen">
                <div class="document-history-header">
                  <span class="text-h5 font-weight-bold">Тексты документа</span>
                  <VBtn icon="ri-arrow-right-s-fill" variant="text" color="secondary" size="small" aria-label="Свернуть" @click="suppressDismissTemporarily('texts-collapse'); isTextsOpen = false" />
                </div>
                <DocumentTexts v-if="props.isOpen && props.documentId" :key="`${props.documentId}-${lastVersion?.id}`" :document-id="props.documentId" />
              </template>
              <template v-else-if="isHistoryOpen">
                <div class="document-history-header">
                  <span class="text-h5 font-weight-bold">История редакций</span>
                  <VTooltip text="Свернуть" location="top">
                    <template #activator="{ props: tp }">
                      <VBtn
                        v-bind="tp"
                        icon="ri-arrow-right-s-fill"
                        variant="text"
                        color="secondary"
                        size="small"
                        aria-label="Свернуть"
                        @click="suppressDismissTemporarily('history-collapse'); closeHistoryPane()"
                      />
                    </template>
                  </VTooltip>
                </div>

                <div class="document-history-content">
                  <VDataTable
                    :items="sortedVersions"
                    :headers="[
                      { title: 'Редакция', key: 'edition', width: '120px' },
                      { title: 'Дата утв.', key: 'approved_at', width: '100px' },
                      { title: 'Дата ввода', key: 'effective_at', width: '100px' },
                      { title: 'Дата истеч.', key: 'valid_until', width: '100px' },
                    ]"
                    density="compact"
                    class="history-table"
                    hide-default-footer
                    :row-props="({ item }) => ({
                      style: item.file ? 'cursor: pointer' : undefined,
                      title: item.file ? 'Скачать файл редакции' : undefined,
                    })"
                    @click:row="onHistoryRowClick"
                  >
                    <template #item.edition="{ item }">
                      <VChip
                        v-if="getDocumentEdition(item)"
                        size="small"
                        variant="flat"
                        class="document-version-chip"
                        :class="{
                          'document-version-chip--current': item === lastVersion,
                        }"
                      >
                        {{ formatRevisionLabel(getDocumentEdition(item)) }}
                      </VChip>
                      <span v-else>—</span>
                    </template>
                    <template #item.approved_at="{ item }">
                      {{ formatDateShort(item.approved_at) }}
                    </template>
                    <template #item.effective_at="{ item }">
                      {{ formatDateShort(item.effective_at) }}
                    </template>
                    <template #item.valid_until="{ item }">
                      {{ formatDateShort(item.valid_until) }}
                    </template>
                  </VDataTable>
                </div>

                <div class="document-history-footer">
                  <div class="document-history-meta">
                    <div v-if="documentDetails?.created_by" class="document-history-meta__row">
                      <VIcon icon="ri-user-line" size="16" class="me-1" />
                      <span>Добавил: {{ getFullName(documentDetails.created_by) || '—' }}</span>
                    </div>
                    <div v-if="lastVersion?.created_at" class="document-history-meta__row">
                      <VIcon icon="ri-time-line" size="16" class="me-1" />
                      <span>Дата и время добавления: {{ formatDateTime(lastVersion.created_at) }}</span>
                    </div>
                  </div>
                  <VBtn
                    v-if="canEdit"
                    variant="outlined"
                    color="primary"
                    class="text-none"
                    prepend-icon="ri-add-line"
                    @click="isAddRevisionModalOpen = true"
                  >
                    Добавить новую редакцию
                  </VBtn>
                </div>
              </template>

              <template v-else>
                <div class="document-history-header">
                  <span class="text-h5 font-weight-bold">Лист ознакомления</span>
                  <VTooltip text="Свернуть" location="top">
                    <template #activator="{ props: tp }">
                      <VBtn
                        v-bind="tp"
                        icon="ri-arrow-right-s-fill"
                        variant="text"
                        color="secondary"
                        size="small"
                        aria-label="Свернуть"
                        @click="suppressDismissTemporarily('acknowledgement-collapse'); closeAcknowledgementPane()"
                      />
                    </template>
                  </VTooltip>
                </div>

                <div class="document-history-content">
                  <div
                    v-if="isAcknowledgementLoading"
                    class="d-flex justify-center align-center py-8"
                  >
                    <VProgressCircular
                      indeterminate
                      color="primary"
                      size="32"
                    />
                  </div>
                  <VDataTable
                    v-else
                    :items="acknowledgementItems"
                    :headers="[
                      { title: 'Сотрудник', key: 'employeeName', width: '172px' },
                      { title: 'Отделение', key: 'departmentName' },
                      { title: 'Дата', key: 'acknowledgedAt', width: '134px' },
                      { title: 'Статус', key: 'status', width: '66px', align: 'center' as const },
                    ]"
                    density="compact"
                    class="history-table acknowledgement-table"
                    hide-default-footer
                    no-data-text="Нет назначенных сотрудников"
                  >
                    <template #item.employeeName="{ item }">
                      {{ item.employeeName }}
                    </template>
                    <template #item.departmentName="{ item }">
                      {{ item.departmentName }}
                    </template>
                    <template #item.acknowledgedAt="{ item }">
                      {{ formatAcknowledgementDate(item.acknowledgedAt) || ' ' }}
                    </template>
                    <template #item.status="{ item }">
                      <VIcon
                        v-if="item.status === 'acknowledged'"
                        icon="ri-checkbox-circle-line"
                        size="20"
                        color="success"
                      />
                      <VIcon
                        v-else
                        icon="ri-indeterminate-circle-line"
                        size="20"
                        color="secondary"
                      />
                    </template>
                  </VDataTable>
                </div>

                <div class="document-history-footer document-acknowledgement-footer">
                  <VBtn
                    v-if="canEdit"
                    variant="outlined"
                    color="primary"
                    class="text-none"
                    prepend-icon="ri-edit-line"
                    :disabled="isAcknowledgementLoading || isAssigningFamiliarizers"
                    :loading="isAssigningFamiliarizers"
                    @click="suppressDismissTemporarily('acknowledgement-edit'); onEditAcknowledgementClick()"
                  >
                    Редактировать
                  </VBtn>
                  <VBtn
                    color="primary"
                    class="text-none"
                    prepend-icon="ri-checkbox-line"
                    :disabled="isAcknowledgementLoading || isAcknowledging"
                    :loading="isAcknowledging"
                    @click="suppressDismissTemporarily('acknowledgement-confirm'); onAcknowledgeClick()"
                  >
                    Ознакомлен
                  </VBtn>
                </div>
              </template>
            </div>
          </Transition>
        </Teleport>
      </div>
    </VCard>
  </VDialog>

  <EmployeeSelectionModal
    ref="employeeModalRef"
    :employees="apiStore.employees"
    :selected-employees="selectedDevelopers"
    :multiple="true"
    title="Выберите разработчиков"
    @select-multiple="onDevelopersSelected"
  />

  <EmployeeSelectionModal
    ref="familiarizerModalRef"
    :employees="apiStore.employees"
    :selected-employees="selectedFamiliarizerEmployees"
    :multiple="true"
    title="Назначить на ознакомление"
    @select-multiple="onFamiliarizersSelected"
  />

  <ParticipantSelectionModal
    ref="participantModalRef"
    title="Выбор участников для доступа"
    :selected-employees="selectedEmployees"
    :selected-departments="selectedDepartments"
    :selected-positions="selectedPositions"
    @select="onAccessParticipantsSelected"
  />

  <FolderSelectionModal
    ref="folderModalRef"
    :folders="selectableFolders"
    :selected-folder-id="formData.folder_id"
    @select="onFolderSelected"
  />

  <EmployeeProfileSidebar
    v-model="isEmployeeProfileOpen"
    :employee-id="employeeProfileId"
  />

  <DocumentAddVersionModal
    v-model:is-open="isAddRevisionModalOpen"
    :document-id="documentId ?? null"
    :document-name="documentDetails?.name"
    :current-revision="currentRevisionLabel"
    @added="handleRevisionAdded"
  />

  <VDialog
    v-model="isDeleteConfirmOpen"
    max-width="532"
    persistent
  >
    <VCard class="pa-5">
      <VCardTitle class="text-h5 font-weight-bold mb-3">
        Вы уверены, что хотите удалить этот документ?
      </VCardTitle>
      <VCardText class="text-body-1">
        Его невозможно будет восстановить.
      </VCardText>
      <VCardActions class="mt-5 justify-end gap-4">
        <VBtn
          variant="outlined"
          color="grey"
          class="px-6"
          @click="isDeleteConfirmOpen = false"
        >
          Отменить
        </VBtn>
        <VBtn
          variant="elevated"
          color="primary"
          class="px-6"
          :loading="isDeleting"
          @click="confirmDeleteDocument"
        >
          Удалить
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.document-form-card {
  overflow: hidden;
}

.document-form-card--edit {
  display: flex;
  flex-direction: column;
  max-block-size: calc(100vh - 48px);

  > .document-form-body,
  > .entity-form-body {
    flex: 1 1 auto;
    min-block-size: 0;
  }
}

@media (max-width: 599px) {
  .document-form-card--edit {
    block-size: 100%;
    max-block-size: 100%;
  }
}

.document-form-body--modal {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  overflow: hidden;
}

.document-form-pane {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  overflow: hidden;

  > .document-form-header {
    flex-shrink: 0;
    position: relative;
    z-index: 6;

    &--side-open {
      padding-block-end: 8px !important;
    }
  }
}

.document-header-action-btn.v-btn {
  border-radius: 50%;

  &--active {
    background-color: rgba(var(--v-theme-primary), 0.12);
  }
}

.document-form-main {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  overflow: hidden;

  > .document-form-card-text {
    flex: 1 1 auto;
    min-block-size: 0;
    min-inline-size: 0;
    overflow-y: auto;
  }
}

.document-history-pane {
  /* Desktop: ровно половина модалки */
  flex: 0 0 50%;
  inline-size: 50%;
  max-inline-size: 50%;
  min-inline-size: 0;
  display: flex;
  flex-direction: column;
  background: rgba(var(--v-theme-primary), 0.04);
  overflow: hidden;

  &--fill {
    flex: 1 1 auto;
    inline-size: 100%;
    max-inline-size: none;
    min-block-size: 0;
    background: rgb(var(--v-theme-surface));
  }
}

.document-side-pane-enter-active,
.document-side-pane-leave-active {
  transition: transform 0.28s ease;
}

.document-side-pane-enter-from,
.document-side-pane-leave-to {
  transform: translateX(100%);
}

.document-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px;
}

.document-history-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0 24px;
  overflow-y: auto;
}

.document-history-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px 24px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  flex-shrink: 0;
}

.document-history-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.document-history-meta__row {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.history-filename-link {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.history-table {
  :deep(.v-data-table__wrapper) {
    table {
      width: 100%;
    }
  }

  :deep(.v-data-table__th) {
    background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
    position: relative;
    padding-inline: 1rem;
    font-weight: 500;
    font-size: 13px;
    white-space: nowrap;
    text-transform: none;
    letter-spacing: normal;
  }

  :deep(.v-data-table__th:not(:last-child))::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 2px;
    height: 14px;
    background-color: rgba(var(--v-theme-on-background), var(--v-custom-opacity, 0.12));
  }

  :deep(.v-data-table__td) {
    padding-inline: 1rem;
    padding-block: 0.75rem;
    font-size: 13px;
  }

  :deep(.v-data-table__tr) {
    cursor: pointer;
  }

  :deep(.v-data-table__tr):hover {
    box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
    background-color: var(--table-row-hover, rgba(var(--v-theme-on-surface), 0.02)) !important;
  }
}

.acknowledgement-table {
  :deep(.v-data-table__tr) {
    cursor: default;
  }

  :deep(.v-data-table__tr):hover {
    box-shadow: none;
    background-color: transparent !important;
  }
}

.document-acknowledgement-footer {
  justify-content: flex-end;
}

.document-form-card--create {
  border-radius: 12px;

  .document-form-footer {
    border-top: none;
  }
}

.document-form-header__title {
  min-inline-size: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.document-form-header__title-wrap {
  min-inline-size: 0;
}

.document-form-header__name {
  width: 100%;
  max-width: 950px;
  min-inline-size: 0;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.hover-editable-text {
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
}

.hover-editable-text:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.document-title-inline-input {
  max-width: 950px;

  :deep(input) {
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.4;
    padding-block: 0;
  }
}

.form_card {
  min-inline-size: 0;
  max-inline-size: 100%;
}

.document-status-chip {
  block-size: 22px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;

  :deep(.v-chip__underlay),
  :deep(.v-chip__overlay) {
    opacity: 0;
  }
}

.document-version-chip {
  block-size: 24px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;
  background: rgba(138, 141, 147, 0.16) !important;
  color: #8A8D93 !important;

  :deep(.v-chip__underlay),
  :deep(.v-chip__overlay) {
    opacity: 0;
  }

  &--current {
    background: rgba(22, 177, 255, 0.16) !important;
    color: #16B1FF !important;
  }
}

.document-form-footer--edit {
  gap: 12px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgb(var(--v-theme-surface));
}

.document-form-footer__actions {
  flex-shrink: 0;
}

.document-form-icon-btn {
  width: 40px;
  height: 36px;
  min-width: 40px;
  border-radius: 6px;

  :deep(.v-btn__overlay),
  :deep(.v-btn__underlay) {
    border-radius: 6px;
  }

  &--delete {
    color: rgb(var(--v-theme-error)) !important;
    background: rgba(var(--v-theme-error), 0.12) !important;
  }
}

.document-form-card :deep(.v-card__loader) {
  flex-shrink: 0;
}

.entity-form-pane :deep(.v-card__loader) {
  max-inline-size: 100%;
}

.document-form-card-text {
  min-height: 0;

  &--edit {
    padding: 0 !important;
  }
}

.entity-form-pane :deep(.v-card-text.document-form-card-text) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.entity-form-pane :deep(.document-form) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.document-form-footer {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  flex-shrink: 0;
  background: rgb(var(--v-theme-surface));
}

.file-card {
  min-width: 0;
  max-width: 100%;
  background: rgba(var(--v-theme-primary), 0.08);
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-primary), 0.12);
}

.file-card--openable {
  cursor: pointer;

  &:hover {
    background: rgba(var(--v-theme-primary), 0.12);
  }
}

.file-card-content {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.file-card-name {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.form-row-two-cols {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.document-form .form-row-two-cols {
  :deep(.v-field__input) {
    align-items: center;
  }
}

.form-row-three-cols {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

:global(.entity-form-embedded.entity-form-embedded--no-chat > .v-card.document-form-card),
:global(.entity-form-embedded.entity-form-embedded--no-chat > * > .v-card.document-form-card) {
  inline-size: 100% !important;
  max-inline-size: 100% !important;
  min-inline-size: 100% !important;
}

.file-preview {
  margin-top: -8px;
  min-width: 0;
  max-width: 100%;
}

.folder-breadcrumbs {
  width: 100%;
  min-inline-size: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 600px;
}

.document-form-footer,
.document-form {
  :deep(.v-btn:not(.v-btn--disabled)) {
    cursor: pointer;
  }

  :deep(.v-btn--disabled) {
    cursor: not-allowed;
  }
}

.access-section {
  padding: 12px;
  background: rgba(var(--v-theme-surface), 0.5);
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.document-form--create {
  :deep(.v-text-field.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.v-autocomplete.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.v-combobox.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.v-select.v-input.v-input--density-compact:not(.v-textarea) .v-field__input),
  :deep(.date-mask-input .v-field__input) {
    min-block-size: 56px;
    min-height: 56px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  :deep(.v-field) {
    font-size: 14px;
  }

  .upload-file-btn {
    block-size: 56px;
    min-block-size: 56px;
  }
}

.upload-file-btn {
  min-height: 40px;
}

.file-validation-field {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
}

.access-radio-group {
  :deep(.v-selection-control-group) {
    gap: 24px;
  }
}

.additional-info-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.87);
  font-size: 14px;
  cursor: pointer;
}

.additional-info-panel {
  overflow: hidden;
  padding-top: 8px;
}

.attachments-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  .file-preview {
    margin-top: 0;
  }

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.attachments-show-all {
  display: inline-flex;
  align-items: center;
  align-self: flex-end;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;

  &:hover {
    color: rgba(var(--v-theme-on-surface), 0.87);
  }
}

.main-document-preview {
  margin-top: 0;
}

.main-document-remove-btn {
  color: rgba(var(--v-theme-on-surface), 0.45) !important;

  :deep(.v-icon) {
    color: rgba(var(--v-theme-on-surface), 0.45);
  }
}

.add-attachments-btn {
  min-height: 40px;
  justify-content: center;
  color: rgba(var(--v-theme-on-surface), 0.6) !important;
  border-color: rgba(var(--v-theme-on-surface), 0.22) !important;

  :deep(.v-btn__content) {
    gap: 10px;
  }

  :deep(.v-btn__overlay),
  :deep(.v-btn__underlay) {
    opacity: 0;
  }

  &:hover {
    color: rgba(var(--v-theme-on-surface), 0.75) !important;
    border-color: rgba(var(--v-theme-on-surface), 0.32) !important;
    background: rgba(var(--v-theme-on-surface), 0.04) !important;
  }
}

.add-attachments-btn__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.38);
  border-radius: 4px;
  flex-shrink: 0;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.limited-access-panel {
  margin-top: -4px;
  max-width: 1004px;
}

:deep(.v-btn) {
  text-transform: none;
  letter-spacing: normal;
}

:deep(.v-field) {
  font-size: 14px;
}

:deep(.v-label) {
  font-size: 14px;
}
</style>

<style lang="scss">
/* content-class вешается на VDialog снаружи scoped-дерева */
.document-form-dialog--mobile {
  display: flex !important;
  flex-direction: column;
  block-size: 100% !important;
  max-block-size: 100% !important;
  margin: 0 !important;

  > .v-card {
    flex: 1 1 auto;
    min-block-size: 0;
    max-block-size: 100%;
  }
}
</style>
